import { Box, FormControl, FormControlLabel, FormLabel, Stack, Typography } from '@mui/material';
import { ReactElement, ReactNode, useId } from 'react';
import FormHelperText from '@mui/material/FormHelperText';
import { compact } from 'lodash';
import { FillLabel } from '../field-types/fillInput';

const multilineTextSx = { whiteSpace: 'pre-line', overflowWrap: 'anywhere' } as const;

export const FormHeader = ({ title, description }: { title: string; description?: string }) => {
  return (
    <Stack useFlexGap component="header" spacing={1}>
      <Typography variant="h4" component="h1" sx={{ overflowWrap: 'anywhere' }}>
        {title}
      </Typography>
      {description && (
        <Typography color="text.secondary" sx={multilineTextSx}>
          {description}
        </Typography>
      )}
    </Stack>
  );
};

interface SectionHeadingProps {
  title: string;
  description?: string;
}

export const SectionHeading = ({ title, description }: SectionHeadingProps) => {
  const hasTitle = title.trim() !== '';
  if (!hasTitle && !description) return null;
  return (
    <Stack useFlexGap spacing={1}>
      {hasTitle && (
        <Typography variant="h5" component="h2" sx={{ overflowWrap: 'anywhere' }}>
          {title}
        </Typography>
      )}
      {description && (
        <Typography color="text.secondary" sx={multilineTextSx}>
          {description}
        </Typography>
      )}
    </Stack>
  );
};

export const StaticTextBlock = ({ text }: { text: string }) => {
  return <Typography sx={multilineTextSx}>{text}</Typography>;
};

interface FieldFrameIds {
  inputId: string;
  labelId: string;
  describedBy: string | undefined;
}

interface FieldFrameProps {
  label: ReactNode;
  labelKind: FillLabel;
  required: boolean;
  description?: string;
  error?: string;
  onLeave?: () => void;
  children: (ids: FieldFrameIds) => ReactElement;
}

export const FieldFrame = (props: FieldFrameProps) => {
  const { label, required, description, error, labelKind, children } = props;
  const id = useId();
  const descriptionId = `${id}description`;
  const errorId = `${id}error`;
  const describedBy = compact([description ? descriptionId : '', error ? errorId : '']).join(' ');
  const ids: FieldFrameIds = { inputId: `${id}input`, labelId: `${id}label`, describedBy: describedBy || undefined };
  const input = children(ids);
  const heading = labelKind === 'heading';
  const group = labelKind === 'legend' || heading;
  const helperTexts = (
    <>
      {description && (
        <FormHelperText id={descriptionId} error={false} sx={multilineTextSx}>
          {description}
        </FormHelperText>
      )}
      {error && (
        <FormHelperText id={errorId} error>
          {error}
        </FormHelperText>
      )}
    </>
  );

  return (
    <FormControl component="div" variant="standard" fullWidth required={required} error={Boolean(error)}>
      {labelKind === 'outlined' && input}
      {labelKind === 'beside' && (
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <FormControlLabel id={ids.labelId} htmlFor={ids.inputId} required={required} control={input} label={label} />
        </Stack>
      )}
      {labelKind === 'label' && (
        <>
          <FormLabel id={ids.labelId} htmlFor={ids.inputId} sx={{ mb: 1 }}>
            {label}
          </FormLabel>
          {input}
        </>
      )}
      {group && (
        <>
          <FormLabel sx={{ mb: 1 }} component={heading ? 'h3' : 'legend'} id={ids.labelId}>
            {label}
          </FormLabel>
          {heading && (description || error) && <Box sx={{ mb: 1 }}>{helperTexts}</Box>}
          {input}
        </>
      )}
      {!heading && helperTexts}
    </FormControl>
  );
};
